// Story 13.2 (frontend) — Panel embebido en la página del bot para
// configurar credenciales del proveedor (cloud-api o evolution-go).
// Story 16.4 — las credenciales son de la organización del bot (antes, del
// usuario logueado: un admin que miraba el bot de otro editaba las suyas).
// Los valores se cifran en el backend y nunca vuelven en el GET (solo `last4`).

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, KeyRound } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { EvolutionGoNotice } from '@/components/whatsapp-connection/EvolutionGoNotice';
import { api } from '@/lib/api-client';

type Provider = 'cloud-api' | 'evolution-go';

interface CredentialEntry {
  provider: string;
  last4: string;
  has_value: boolean;
  updatedAt: string;
  createdAt: string;
}

interface Props {
  /** Provider del bot que estamos viendo. Si es cloud-api mostramos los
   *  campos de Meta; si es evolution-go los de Evolution Go. */
  provider: Provider;
  /** La organización del bot: sus credenciales las usan todos sus bots. */
  organization: { id: number; name: string };
}

// Story 13.x cleanup — los identificadores operacionales (baseUrl,
// instanceName, phoneNumberId) ahora se piden en el wizard de creación y
// viven en `bot.config.whatsapp`. Acá solo solicitamos los SECRETS, que
// se cifran y persisten en `organization_credentials`. Si el operador necesita
// cambiar baseUrl/instanceName, edita el bot YAML en el editor (epic 11).
const FIELDS: Record<Provider, Array<{ key: string; label: string; sensitive: boolean }>> = {
  'cloud-api': [{ key: 'accessToken', label: 'Access Token (Meta Cloud API)', sensitive: true }],
  // Story 17-3 — sin "webhook secret": el webhook de Evolution GO se
  // autentica con esta misma API key (el `instanceToken` que manda).
  'evolution-go': [{ key: 'apiKey', label: 'API Key de la instancia', sensitive: true }],
};

export function CredentialsPanel({ provider, organization }: Props): React.ReactElement {
  const queryClient = useQueryClient();
  const [values, setValues] = useState<Record<string, string>>({});
  const [showSensitive, setShowSensitive] = useState<Record<string, boolean>>({});
  const queryKey = ['organization-credentials', organization.id];
  const baseUrl = `/api/v1/organizations/${organization.id}/credentials`;

  const credsQuery = useQuery({
    queryKey,
    queryFn: () => api.get<CredentialEntry[]>(baseUrl),
  });

  const upsertMutation = useMutation({
    mutationFn: (body: { provider: Provider; values: Record<string, string> }) =>
      api.put(`${baseUrl}/${body.provider}`, { values: body.values }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      toast.success('Credenciales guardadas. Activa el bot para empezar a usarlas.');
      setValues({});
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : 'Error guardando credenciales');
    },
  });

  const existing = credsQuery.data?.find((c) => c.provider === provider);
  const fields = FIELDS[provider];

  function handleSubmit(e: React.FormEvent): void {
    e.preventDefault();
    const filtered: Record<string, string> = {};
    for (const [k, v] of Object.entries(values)) {
      if (v && v.trim().length > 0) filtered[k] = v;
    }
    if (Object.keys(filtered).length === 0) {
      toast.error('Llena al menos un campo');
      return;
    }
    upsertMutation.mutate({ provider, values: filtered });
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <KeyRound className="h-4 w-4" aria-hidden />
          Credenciales — {provider}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Son de la organización <strong>{organization.name}</strong>: las usan todos sus bots.
        </p>
        {/* Story 17-2 — aviso de uso que pide la licencia de Evolution GO. */}
        {provider === 'evolution-go' && <EvolutionGoNotice />}
        {existing && (
          <div className="flex items-center gap-2 rounded-md border border-success/30 bg-success/5 p-3 text-sm">
            <CheckCircle2 className="h-4 w-4 text-success" aria-hidden />
            <span>
              Configurado. Last4: <code className="font-mono">{existing.last4}</code> · Actualizado:{' '}
              {new Date(existing.updatedAt).toLocaleString()}
            </span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-3">
          {fields.map((f) => {
            const isSensitive = f.sensitive;
            const isShown = showSensitive[f.key] ?? false;
            return (
              <div key={f.key}>
                <label htmlFor={`cred-${f.key}`} className="mb-1 block text-sm font-medium">
                  {f.label}
                </label>
                <div className="flex gap-2">
                  <input
                    id={`cred-${f.key}`}
                    type={isSensitive && !isShown ? 'password' : 'text'}
                    className="flex-1 rounded-md border border-border bg-background px-3 py-2 text-sm font-mono"
                    value={values[f.key] ?? ''}
                    onChange={(e) => setValues((v) => ({ ...v, [f.key]: e.target.value }))}
                    placeholder={existing ? '••••••••• (sin cambios)' : ''}
                    autoComplete="off"
                  />
                  {isSensitive && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setShowSensitive((s) => ({ ...s, [f.key]: !s[f.key] }))}
                    >
                      {isShown ? 'Ocultar' : 'Mostrar'}
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
          <div className="flex justify-end gap-2">
            <Button type="submit" disabled={upsertMutation.isPending}>
              {upsertMutation.isPending
                ? 'Guardando…'
                : existing
                  ? 'Actualizar credenciales'
                  : 'Guardar credenciales'}
            </Button>
          </div>
        </form>

        <p className="text-xs text-muted-foreground">
          Las credenciales se cifran con AES-256-GCM en el servidor antes de persistir. La master
          key vive solo en variables de entorno del backend; ni la base de datos ni la respuesta del
          servidor exponen los valores en plano. Solo se muestra <code>last4</code> como
          confirmación visual.
        </p>
      </CardContent>
    </Card>
  );
}
